import React, { Fragment, isValidElement, JSXElementConstructor, ReactElement, useState } from 'react';
import { cn } from 'src/domains/common/components/utils';
import { Badge } from 'src/domains/common/components/badge/badge';
import { SportsPickerTreeType } from './action-conditions/sportsbook-sections/sports-picker/utils';
import { GeneralDetailsState } from './general-details/GeneralDetails.state';
import { PlayerManagementState } from './player-management/PlayerManagement.state';
import { RewardConditionsState } from './reward-conditions/RewardConditions.state';
import { Tooltip } from 'src/components/Tooltip/Tooltip';
import { InfoIcon } from 'lucide-react';
import { ActionConditionsState } from './action-conditions/ActionConditions.state';
import { CampaignUpdateFormType } from 'src/api_openapi/v2/bonus-engine/schemas';
import { Amount } from 'src_common/common/amount/Amount';
import {
    FreeBetRewardType,
    FreeSpinRewardType,
} from 'src/domains/casino/webview/bonus-engine/campaign/reward-conditions/RewardConditions.types';
import { UK_TIMEZONE } from 'src/domains/casino/webview/bonus-engine/campaign/CampaignCreator.helpers';
import { CalendarState } from 'src/domains/common/components/calendar/CalendarState';
import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';

type ItemType = {
    type: string;
    value: string | number | string[];
};

type BetTypeListItemType = {
    item: ItemType;
    index: number;
    allSportsData?: SportsPickerTreeType[];
    games?: CasinoGameType[];
    origin?: 'action' | 'reward';
};

type BetTypeListPropsType = {
    criteria: ItemType[];
    allSportsData?: SportsPickerTreeType[];
    games?: CasinoGameType[];
    origin?: 'action' | 'reward';
};

type CasinoGameType = {
    demo: boolean;
    description: string;
    id: number;
    image: string;
    image_square: string;
    image_vertical: string;
    jackpot_amount: string;
    launch_game_id: string;
    max_bet: string;
    max_win: string;
    min_bet: string;
    name: string;
    provider: string;
    published: boolean;
    return_to_player: string;
    studio_id: string;
    studio_name: string;
    universe: string;
};

export type CampaignPopupPropsType = {
    promoCodes: CampaignUpdateFormType['promoCodes'];
    withPromoCodes: boolean;
    generalDetails: GeneralDetailsState;
    playerManagement: PlayerManagementState;
    actionConditions: ActionConditionsState;
    rewardConditions: RewardConditionsState;
};

const moneyString = (value: number): string => new Amount((value / 100).toString()).format('GBP');

const mapCasinoLaunchIdToName = (games: CasinoGameType[], launchGameId: string): string => {
    const game = games.find((g) => g.launch_game_id === launchGameId);
    return game?.name ?? launchGameId;
};

const mapSportsEventsAndCompIdToName = (
    sportsPickerData: SportsPickerTreeType[],
    id: string,
    type: 'competition' | 'event'
): string | ReactElement<string | JSXElementConstructor<unknown>> | undefined | null => {
    for (const selectedSport of sportsPickerData) {
        if (selectedSport.children !== undefined && selectedSport.children.length > 0) {
            for (const competition of selectedSport.children) {
                if (type === 'competition' && competition.id.includes(`"competitionId":"${id}"`)) {
                    return competition.label;
                }
                if (competition.children !== undefined && competition.children.length > 0) {
                    for (const event of competition.children) {
                        if (type === 'event' && event.id.includes(`"eventId":"${id}"`)) {
                            if (
                                isValidElement<{
                                    children?:
                                        | string
                                        | ReactElement<string | JSXElementConstructor<unknown>>
                                        | undefined
                                        | null;
                                }>(event.label)
                            ) {
                                const { children } = event.label.props;
                                return Array.isArray(children) ? children[0] : children;
                            }
                        }
                    }
                }
            }
        }
    }
    return null;
};

const TooltipDisplay = ({ content, title }: { content: string; title: string }): JSX.Element => (
    <>
        {title}
        <Tooltip content={content}>
            <InfoIcon className='size-3 align-middle ml-1' />
        </Tooltip>
    </>
);

const BetTypeItem = ({ item, allSportsData = [], games, origin }: BetTypeListItemType): JSX.Element => {
    const DISPLAYED_ITEMS_LIMIT = 6;
    const BADGE_DISPLAY_TYPES = new Set([
        'bet-type',
        'sport',
        'event',
        'casino-game',
        'playthrough-games',
        'competition',
    ]);
    const [isExpanded, setIsExpanded] = useState<boolean>(false);
    const { type, value } = item;
    const moneyTypes = ['min-stake', 'max-stake', 'target-stake'];

    return (
        <li
            key={type}
            className='mt-2 first:mt-0'
        >
            <span className='font-bold'>
                {type === 'sport' ? (
                    'eligible sports'
                ) : type === 'competition' ? (
                    <TooltipDisplay
                        content='Entire competition betting, does not mean everything inside this competition is included'
                        title='competition outcome'
                    />
                ) : type === 'event' ? (
                    <TooltipDisplay
                        content='Individual events allowed, restricted by the eligible sports'
                        title='individual events'
                    />
                ) : type === 'playthrough-games' ? (
                    <TooltipDisplay
                        content='Games required by the user to play until the condition has been met'
                        title='Playthrough Games'
                    />
                ) : type === 'casino-game' ? (
                    origin === 'reward' ? (
                        <TooltipDisplay
                            content='Games that the rewarded free spins will be playable on'
                            title='rewarded games'
                        />
                    ) : (
                        'casino games'
                    )
                ) : (
                    type.replaceAll('-', ' ')
                )}
                :
            </span>
            {BADGE_DISPLAY_TYPES.has(type) ? (
                <div className='flex flex-wrap gap-2 pb-1 pt-1'>
                    {Array.isArray(item.value) ? (
                        <>
                            {(isExpanded ? item.value : item.value.slice(0, DISPLAYED_ITEMS_LIMIT)).map((v) => {
                                let label: React.ReactNode = v;
                                if (type === 'competition') {
                                    label = mapSportsEventsAndCompIdToName(allSportsData, v, 'competition') ?? v;
                                } else if (type === 'event') {
                                    label = mapSportsEventsAndCompIdToName(allSportsData, v, 'event') ?? v;
                                } else if (
                                    (type === 'playthrough-games' || type === 'casino-game') &&
                                    games !== undefined
                                ) {
                                    label = mapCasinoLaunchIdToName(games, v);
                                }
                                return (
                                    <Badge
                                        key={v}
                                        variant='secondary'
                                    >
                                        {label}
                                    </Badge>
                                );
                            })}
                            {item.value.length > DISPLAYED_ITEMS_LIMIT && (
                                <Badge
                                    className='hover:text-primary hover:underline'
                                    variant='outline'
                                    onClick={() => setIsExpanded(!isExpanded)}
                                >
                                    ...Show {isExpanded ? 'less' : ` ${item.value.length - DISPLAYED_ITEMS_LIMIT} more`}
                                </Badge>
                            )}
                        </>
                    ) : (
                        <Badge variant='secondary'>{item.value}</Badge>
                    )}
                </div>
            ) : (
                <span className='ml-1'>
                    {Array.isArray(item.value)
                        ? item.value.join(', ')
                        : moneyTypes.includes(type) && typeof value === 'number'
                          ? moneyString(value)
                          : item.value}
                </span>
            )}
        </li>
    );
};

const BetTypeList = ({ criteria, allSportsData, games, origin }: BetTypeListPropsType): JSX.Element => {
    if (!Array.isArray(criteria)) {
        return <Badge variant='secondary'>{criteria}</Badge>;
    }
    return (
        <div className='flex flex-col'>
            {criteria.map((item, index) => BetTypeItem({ item, index, allSportsData, games, origin }))}
        </div>
    );
};

const EligibleUsersList = ({ ids }: { ids: number[] }): JSX.Element => {
    const DISPLAYED_ITEMS_LIMIT = 6;
    const [isExpanded, setIsExpanded] = useState<boolean>(false);

    return (
        <div className='flex flex-col'>
            <div className='flex flex-wrap gap-2 pb-1 pt-1'>
                {(isExpanded ? ids : ids.slice(0, DISPLAYED_ITEMS_LIMIT)).map((v) => {
                    return (
                        <Badge
                            key={v}
                            variant='secondary'
                        >
                            {v}
                        </Badge>
                    );
                })}
                {ids.length > DISPLAYED_ITEMS_LIMIT && (
                    <Badge
                        className='hover:text-primary hover:underline'
                        variant='outline'
                        onClick={() => setIsExpanded(!isExpanded)}
                    >
                        ...Show {isExpanded ? 'less' : ` ${ids.length - DISPLAYED_ITEMS_LIMIT} more`}
                    </Badge>
                )}
            </div>
        </div>
    );
};

const RewardAmount = ({ reward }: { reward: FreeBetRewardType | FreeSpinRewardType }): JSX.Element => {
    const { rewardAmount = 0, rewardType } = reward;

    return (
        <li>
            <strong>Reward Amount: </strong>
            <span>{rewardType === 'free-bet' ? moneyString(rewardAmount) : rewardAmount}</span>
        </li>
    );
};

const DateField = ({
    label,
    formInputState,
}: {
    label: string;
    formInputState: FormInputState<string, string>;
}): JSX.Element => {
    const [{ selectedDate }] = useState(() => new CalendarState(formInputState, 'monday', UK_TIMEZONE, false));

    return (
        <li>
            <strong>{label} (UK time): </strong>
            {selectedDate?.format('YYYY/MM/DD HH:mm') ?? '-'}
        </li>
    );
};

export const CampaignPopUp = ({
    promoCodes,
    withPromoCodes,
    generalDetails,
    playerManagement,
    rewardConditions,
    actionConditions,
}: CampaignPopupPropsType): JSX.Element => {
    const noActionCondition = actionConditions.actionConditions === undefined;
    const rewardPlatformState =
        rewardConditions.rewardType === 'free-bet'
            ? rewardConditions.sportsBookSectionState
            : rewardConditions.casinoSectionState;

    return (
        <div className='m-0 gap-2 overflow-y-auto max-h-[700px]'>
            <p className='text-center'>
                Are you sure you want to save and activate this campaign? Once activated, it will be live and visible to
                users. Please confirm that you have reviewed all settings and are ready to proceed. You can review these
                settings below
            </p>
            <hr />
            <div
                className={cn('grid grid-cols-3 gap-3 capitalize', { 'grid-cols-2 auto-cols-auto': noActionCondition })}
            >
                <div className='col-span-1'>
                    <strong className='underline underline-offset-2 flex justify-center'>General Conditions</strong>
                    <ul className='mt-0 space-y-2'>
                        <li className='mt-3'>
                            <strong>Campaign Type: </strong>
                            {generalDetails.campaignType?.replaceAll('-', ' ')}
                        </li>
                        <li>
                            <strong>Campaign Name: </strong> {generalDetails.name}
                        </li>
                        <li className='break-normal'>
                            <strong>Terms & Conditions: </strong>
                            {generalDetails.termsAndConditions?.replace(/(<([^>]+)>)/gi, '')}
                        </li>
                        <li>
                            {typeof generalDetails.optInOnly === 'boolean' && (
                                <span>
                                    <strong>Require User Opt-In: </strong>
                                    {generalDetails.optInOnly ? 'Yes' : 'No'}
                                </span>
                            )}
                        </li>
                        {withPromoCodes && (
                            <div>
                                {promoCodes?.map((el, index) => (
                                    <div
                                        key={el.promoCode}
                                        className='mt-0 space-y-1'
                                    >
                                        <li>
                                            <strong>Promo Code{promoCodes.length > 1 && ` ${index + 1}`}: </strong>
                                            {el.promoCode ?? '-'}
                                        </li>
                                        <ul className='mt-0 space-y-2'>
                                            {el.numberOfUses !== undefined && (
                                                <li>
                                                    <strong>Number of Uses: </strong>
                                                    {el.numberOfUses}
                                                </li>
                                            )}
                                            {el.useLimit !== undefined && (
                                                <li>
                                                    <strong>Use Limit: </strong>
                                                    {el.useLimit}
                                                </li>
                                            )}
                                        </ul>
                                    </div>
                                ))}
                            </div>
                        )}
                        {generalDetails.startAt === undefined ? (
                            <li>
                                <strong>Start Date & Time: </strong> Once Campaign Activated
                            </li>
                        ) : (
                            <DateField
                                label='Start Date & Time'
                                formInputState={generalDetails.startAtForm}
                            />
                        )}
                        <DateField
                            label='End Date & Time'
                            formInputState={generalDetails.expiryAtForm}
                        />
                        <li>
                            <strong>Usage Frequency: </strong>
                            {generalDetails.frequencyPeriod?.replaceAll('-', ' ')}
                        </li>
                        {generalDetails.numberOfUses !== undefined && (
                            <li>
                                <strong>Number of Uses in Frequency: </strong>
                                {generalDetails.numberOfUses}
                            </li>
                        )}
                        <li>
                            <strong>Player Type Allowed: </strong>
                            {playerManagement.playerManagement?.replaceAll('-', ' ') ?? '-'}
                        </li>
                        {playerManagement.maxSf !== undefined && (
                            <li>
                                <strong>Max Customer SF: </strong>
                                {playerManagement.maxSf}
                            </li>
                        )}
                        {playerManagement.minSf !== undefined && (
                            <li>
                                <strong>Min Customer SF: </strong>
                                {playerManagement.minSf}
                            </li>
                        )}
                        {playerManagement.eligibleUsers.length > 0 && (
                            <li>
                                <strong>Eligible Users: </strong>
                                <EligibleUsersList ids={playerManagement.eligibleUsers} />
                            </li>
                        )}
                        {generalDetails.timeLimitTrigger !== undefined && (
                            <>
                                <li>
                                    <strong>Time Limit Trigger: </strong>
                                    {generalDetails.timeLimitTrigger}
                                </li>
                                <li>
                                    <strong>Time Limit Duration (hours): </strong>
                                    {generalDetails.timeLimit}
                                </li>
                            </>
                        )}
                    </ul>
                </div>
                {!noActionCondition && (
                    <div className='col-span-1'>
                        <strong className='flex justify-center underline underline-offset-2'>Action Conditions</strong>
                        <ul className='mt-0 space-y-2'>
                            <li className='mt-3'>
                                <strong>Betting Platform: </strong> {actionConditions.actionConditions.bettingPlatform}
                            </li>
                            {actionConditions.actionConditions.sequenceDependenceOfCriteria.length > 0 && (
                                <li>
                                    <strong>Sequence Dependence of Criteria: </strong>
                                    {actionConditions.actionConditions.sequenceDependenceOfCriteria.join(', ')}
                                </li>
                            )}
                            <BetTypeList
                                criteria={actionConditions.actionConditions.criteria}
                                allSportsData={
                                    actionConditions.sportsbookSelectionsState.sportsPickerState.allSportsForView
                                }
                                games={actionConditions.casinoSelectionsState.gamePickerState.collectionData.games}
                            />
                        </ul>
                    </div>
                )}
                <div className='col-span-1'>
                    <strong className='flex justify-center underline underline-offset-2'>Reward Conditions </strong>
                    <ul className='mt-0 space-y-2'>
                        {rewardConditions.rewardConditions?.map((el, index) => (
                            <Fragment key={index}>
                                <li className='mt-3'>
                                    <strong>Reward Type:</strong> {el.rewardType.replaceAll('-', ' ')}
                                </li>
                                <li>
                                    <strong>Reward Name:</strong> {el.rewardName ?? '-'}
                                </li>
                                <RewardAmount reward={el} />
                                {el.scheduledReward !== undefined && (
                                    <div className='mt-0 space-y-2'>
                                        <li>
                                            <strong>Scheduled reward:</strong>{' '}
                                            {el.scheduledReward === true ? 'Yes' : 'No'}
                                        </li>
                                        {el.scheduledReward === true && (
                                            <DateField
                                                label='Reward Date'
                                                formInputState={rewardPlatformState.rewardDateForm}
                                            />
                                        )}
                                    </div>
                                )}
                                {el.expiryAt !== undefined && (
                                    <DateField
                                        label='Reward Expiry'
                                        formInputState={rewardPlatformState.expiryForm}
                                    />
                                )}
                                {el.relativeExpiryDays !== undefined && (
                                    <li>
                                        <strong>Expires in (Days):</strong> {el.relativeExpiryDays}
                                    </li>
                                )}
                                {el.rewardInterval !== undefined &&
                                    el.installmentCount !== undefined &&
                                    el.installmentCount > 1 && (
                                        <div className='mt-0 space-y-2'>
                                            <li>
                                                <strong>Instalment Count:</strong> {el.installmentCount}
                                            </li>
                                            <li>
                                                <strong>Reward Interval (Days):</strong> {el.rewardInterval}
                                            </li>
                                        </div>
                                    )}
                                <BetTypeList
                                    criteria={el.usageConditions}
                                    allSportsData={
                                        rewardConditions.sportsBookSectionState.sportsPickerState.allSportsForView
                                    }
                                    games={
                                        rewardConditions.casinoSectionState.freeSpinsEligibleGamesState.collectionData
                                            .games
                                    }
                                    origin='reward'
                                />
                                {el.rewardType === 'free-spin' && (
                                    <>
                                        {el.playthrough !== undefined && (
                                            <li>
                                                <strong>Playthrough:</strong> {el.playthrough}
                                            </li>
                                        )}
                                        <li>
                                            <BetTypeList
                                                criteria={[
                                                    {
                                                        type: 'playthrough-games',
                                                        value: el.playthroughEligibleGames,
                                                    },
                                                ]}
                                                games={
                                                    rewardConditions.casinoSectionState.playThroughEligibleGamesState
                                                        .collectionData.games
                                                }
                                                origin='reward'
                                            />
                                        </li>
                                    </>
                                )}
                            </Fragment>
                        ))}
                    </ul>
                </div>
            </div>
            <hr />
        </div>
    );
};
